import { Combobox, ComboboxMultiple } from '../../../Combobox';
import type { Column, ColumnMeta } from '@tanstack/react-table';
import type { DropDownItem } from '../../../../types/dropDownItem';
import { useMemo } from 'react';
import { useDialogContentContainer } from '../../../Dialog/useDialogContentContainer';
import { useFullscreenTableContainer } from '../../hooks/useFullscreenTableContainer';
import useI18n from '../../../../hooks/useI18n';
import { isDefined } from '../../../../lib/utils';

type ColumnFilterOptions = NonNullable<
  ColumnMeta<unknown, unknown>['filterOptions']
>;

interface ColumnFilterComboboxProps<TData, TValue> {
  column: Column<TData, TValue>;
  /**
   * The column's `meta.filterOptions`. Required here, so a select filter cannot
   * render without options: `ColumnMeta` is an interface augmentation, so it
   * cannot tie them to `filterVariant` itself.
   */
  options: ColumnFilterOptions;
}

const itemToStringLabel = (item: DropDownItem) => item.label;
const itemToStringValue = (item: DropDownItem) => item.value;
// Base UI compares selected values by reference. The options are rebuilt
// whenever `filterOptions` changes, so match them by value instead.
const isItemEqualToValue = (item: DropDownItem, value: DropDownItem) =>
  item.value === value.value;

/**
 * Normalises the filter options into option objects, and indexes them by the
 * value the column filter stores.
 */
function useColumnFilterOptions(options: ColumnFilterOptions) {
  const items = useMemo<DropDownItem[]>(
    () =>
      options.map((option) =>
        typeof option === 'string' || typeof option === 'number'
          ? { label: option.toString(), value: option.toString() }
          : option,
      ),
    [options],
  );

  const itemsByValue = useMemo(
    () => new Map(items.map((item) => [item.value, item])),
    [items],
  );

  return { items, itemsByValue };
}

/**
 * Where the popup has to render to stay both visible and interactive.
 *
 * A modal dialog blocks pointer events outside its own content, and a
 * fullscreen table outranks the popup's z-index, so the body is wrong in both
 * cases.
 */
function usePopupContainer() {
  const dialogContainer = useDialogContentContainer();
  const fullscreenContainer = useFullscreenTableContainer();

  return dialogContainer ?? fullscreenContainer;
}

/**
 * The dropdown a `select` or `multiselect` column filter shows in the table
 * filter dialog.
 *
 * The column filter value stays a plain string, or an array of them. It is
 * persisted to localStorage and compared against raw cell values, so the option
 * objects never leave this component.
 *
 * `meta.filterRenderer` draws the options, and for `multiselect` the selected
 * chips as well. The single-select trigger shows the plain label, because
 * `Combobox` has no renderer for its selected value.
 */
export function ColumnFilterCombobox<TData, TValue>({
  column,
  options,
}: ColumnFilterComboboxProps<TData, TValue>) {
  const { t } = useI18n();
  const container = usePopupContainer();

  const { items, itemsByValue } = useColumnFilterOptions(options);
  const filterValue = column.getFilterValue();

  const selectedItems = useMemo(() => {
    const values = Array.isArray(filterValue) ? filterValue.map(String) : [];

    return values.map((value) => itemsByValue.get(value)).filter(isDefined);
  }, [filterValue, itemsByValue]);
  const selectedItem =
    typeof filterValue === 'string'
      ? (itemsByValue.get(filterValue) ?? null)
      : null;

  const filterRenderer = column.columnDef.meta?.filterRenderer;

  const sharedProps = {
    isItemEqualToValue,
    items,
    itemToStringLabel,
    itemToStringValue,
    label: column.columnDef.header?.toString(),
    noItemsText: t('noResultsFound'),
    renderItemLabel: filterRenderer,
    slotProps: {
      content: { container },
      trigger: { className: 'eye:bg-white', showClear: true },
      value: { placeholder: t('value') },
    },
  };

  if (column.columnDef.meta?.filterVariant === 'multiselect') {
    return (
      <ComboboxMultiple<DropDownItem>
        {...sharedProps}
        value={selectedItems}
        onValueChange={(nextItems) =>
          column.setFilterValue(
            nextItems.length ? nextItems.map(itemToStringValue) : undefined,
          )
        }
        renderChipLabel={filterRenderer}
        slotProps={{
          ...sharedProps.slotProps,
          // A column renderer already draws a tag, so the chip must not box it
          // a second time.
          chip: { variant: filterRenderer ? 'bare' : 'default' },
        }}
      />
    );
  }

  return (
    <Combobox<DropDownItem>
      {...sharedProps}
      value={selectedItem}
      onValueChange={(nextItem) => column.setFilterValue(nextItem?.value)}
    />
  );
}
